/**
 * Shared Navigation Component
 * Provides consistent navigation UI across all slides
 * Includes: Previous/Next buttons (top) and Progress dots (bottom)
 */

import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { NavigationProps } from '../../types/slideTypes';

export const SlideNavigation: React.FC<{
  currentSlide: number;
  totalSlides: number;
  onGoToSlide: (index: number) => void;
}> = ({ currentSlide, totalSlides, onGoToSlide }) => {
  const onPrevious = () => onGoToSlide(Math.max(0, currentSlide - 1));
  const onNext = () => onGoToSlide(Math.min(totalSlides - 1, currentSlide + 1));

  return (
    <div
      className="absolute bottom-4 right-4 flex items-center gap-2 rounded-lg p-2"
      style={{
        background: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
    >
      <button
        disabled={currentSlide === 0}
        onClick={onPrevious}
        className="flex items-center gap-2 rounded-md bg-white/20 px-2 py-1 text-white hover:bg-white/30 disabled:opacity-40 transition-colors"
        aria-label="Go to previous slide"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>

      <div className="flex items-center gap-1 px-2">
        {Array.from({ length: totalSlides }, (_, index) => (
          <button
            key={index}
            onClick={() => onGoToSlide(index)}
            className={`w-2 h-2 rounded-full transition-all ${
              index === currentSlide ? 'bg-white w-6' : 'bg-white/40 hover:bg-white/60'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      <span className="text-white text-sm px-2">
        {currentSlide + 1} / {totalSlides}
      </span>

      <button
        disabled={currentSlide === totalSlides - 1}
        onClick={onNext}
        className="flex items-center gap-2 rounded-md bg-white/20 px-2 py-1 text-white hover:bg-white/30 disabled:opacity-40"
        aria-label="Go to next slide"
      >
        <ChevronRight className="w-5 h-5" />
      </button>
    </div>
  );
};

// BottomNavigation component that accepts NavigationProps
export const BottomNavigation: React.FC<NavigationProps> = ({
  currentSlide,
  totalSlides,
  onPrevious,
  onNext,
  onGoToSlide,
}) => {
  return (
    <div
      className="absolute bottom-4 right-4 flex items-center gap-2 rounded-lg p-2"
      style={{
        background: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
    >
      <button
        disabled={currentSlide === 0}
        onClick={onPrevious}
        className="flex items-center gap-2 rounded-md bg-white/20 px-2 py-1 text-white hover:bg-white/30 disabled:opacity-40 transition-colors"
        aria-label="Go to previous slide"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>

      <div className="flex items-center gap-1 px-2">
        {Array.from({ length: totalSlides }, (_, index) => (
          <button
            key={index}
            onClick={() => onGoToSlide(index)}
            className={`w-2 h-2 rounded-full transition-all ${
              index === currentSlide ? 'bg-white w-6' : 'bg-white/40 hover:bg-white/60'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      <span className="text-white text-sm px-2">
        {currentSlide + 1} / {totalSlides}
      </span>

      <button
        disabled={currentSlide === totalSlides - 1}
        onClick={onNext}
        className="flex items-center gap-2 rounded-md bg-white/20 px-2 py-1 text-white hover:bg-white/30 disabled:opacity-40"
        aria-label="Go to next slide"
      >
        <ChevronRight className="w-5 h-5" />
      </button>
    </div>
  );
};
